import { Auth } from "aws-amplify";
import BlockyHeavyText from "components/custom-texts/blocky-heavy-text.tsx";
import { useAppSelector } from "state/hooks.ts";
import { Button } from "react-bootstrap";
import { useEffect, useState } from "react";
import { getPrizeoutSettingsPath, } from "utils/backend-path-builders.ts";
import { getRequest } from "utils/httpClient.ts";
import { Mixpanel } from "hooks/mixPanel.tsx";
import PrizeoutSettings from "interfaces/prizeout-settings.ts";
import { getUserState } from "state/slices/user-slice.ts";

interface Props {
  onExit: () => void;
  walletId: string;
  groupId: string;
}

const PrizeoutButton = ({ onExit, walletId, groupId }: Props) => {

  const user = useAppSelector(getUserState);

  const [prizeoutSettings, setPrizeoutSettings] = useState<PrizeoutSettings>();
  const [prizeoutIsLoading, setPrizeoutIsLoading] = useState(false);

  useEffect(() => {
    if (!groupId) return;

    getRequest(getPrizeoutSettingsPath(groupId), { skipIntegrationApi: true }).then(
      (data: PrizeoutSettings) => {
        setPrizeoutSettings(data);
      }
    );
  }, [groupId]);

  const launchPrizeout = async () => {
    if (!prizeoutSettings || prizeoutIsLoading) {
      return;
    }

    setPrizeoutIsLoading(true);

    try {
      // First, try to refresh the authentication token
      // This ensures Prizeout receives the most up-to-date user balance information
      // by bypassing any cached token data on Prizeout side
      await Auth.currentAuthenticatedUser({ bypassCache: true });
    } catch (error) {
      console.error("Error refreshing authentication token:", error);
    }

    try {
      const currentSession = await Auth.currentSession();
      const token = currentSession.getIdToken().getJwtToken();

      const params = {
        env: process.env.REACT_APP_ENVIRONMENT === "prod" ? "prod" : "sandbox",
        partner_id: prizeoutSettings.partner_id,
        session_id: JSON.stringify({
          jwt: token,
          context: {
            wallet_id: walletId,
            group_id: groupId
          }
        }),
        callbacks: {
          onInit: () => {
            Mixpanel.track("Prizeout opened", { id: user.id });
            setPrizeoutIsLoading(false);
          },
          onClose: () => {
            onExit();
            setPrizeoutIsLoading(false);
          },
        },
      };

      (window as any).prizeoutSDK.init(params);
    } catch (error) {
      console.error("Error initializing Prizeout:", error);
      setPrizeoutIsLoading(false);
    }
  };

  if (!prizeoutSettings?.partner_id) return <></>;

  return (
    <Button
      onClick={launchPrizeout}
      size="lg"
      disabled={prizeoutIsLoading}
      style={{
        padding: '6px 24px',
        fontSize: '18px',
        fontWeight: 'bold',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
        border: '2px solid #007bff',
        background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
        transform: prizeoutIsLoading ? 'none' : 'translateY(-2px)',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!prizeoutIsLoading) {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!prizeoutIsLoading) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
        }
      }}
    >
      <BlockyHeavyText>{prizeoutIsLoading ? "Loading..." : "Gift Cards"}</BlockyHeavyText>
    </Button>
  );
};

export default PrizeoutButton;
